import React, { createContext, FC, Fragment, useContext } from 'react';
import get from 'lodash/get';
import some from 'lodash/some';
import styled from 'styled-components';

import PhoneCallPopover from 'src/presentation/components/pages/ivr/components/PhoneCallPopover';
import { Button, Tooltip } from 'src/presentation/components/agent_desktop_components';
import { IvrConfiguration } from 'src/presentation/components/pages/ivr/components/ivrTree/config/ivrConfig';
import { VersionBar } from './VersionBar';
import { DomainError } from 'src/models/DomainError';

export const VersionManagerContext = createContext<{
  backToLatest?: Function;
  changeVersion?: Function;
  isSaveInProgress?: boolean;
  items: IvrConfiguration[];
  publishIvr?: Function;
  restoreIvr?: Function;
  selected: number;
  isNew: boolean;
}>({
  items: [],
  selected: 0,
  isNew: false,
});

type VersionManagerProps = {
  'data-aid'?: string;
  errors?: DomainError[];
};

const VersionManager: FC<VersionManagerProps> = ({ 'data-aid': dataAid, errors = [] }) => {
  const { isSaveInProgress, items, backToLatest, restoreIvr, selected, publishIvr } = useContext(VersionManagerContext);

  const latestVersion = get(items, '[0].version');
  const isArchived = get(items, '[0].archivedAt');
  const isPublished = some(items, item => item.publishedAt && item.version === selected);
  const isConfigurationInvalid = !!errors.length;

  const isDisabled = isSaveInProgress || isConfigurationInvalid || isArchived;

  const renderPublishButton = () => (
    <PublishButton dataAid={`${dataAid}-publishButton`} onPublish={publishIvr} disabled={isDisabled || isPublished} />
  );

  const renderPublishButtonWithTooltip = () => {
    return isConfigurationInvalid ? (
      <StyledTooltip
        position="bottom"
        margin={20}
        message={
          <span data-aid={`${dataAid}-publishButton-tooltip`}>
            You won&apos;t be able to publish
            <br /> until you fix all errors
          </span>
        }
      >
        {renderPublishButton()}
      </StyledTooltip>
    ) : (
      renderPublishButton()
    );
  };

  return selected === latestVersion ? (
    <Fragment>
      <VersionBar data-aid={`${dataAid}-version-bar`} />
      <PhoneCallPopover isDisabled={!!isDisabled} />
      {renderPublishButtonWithTooltip()}
    </Fragment>
  ) : (
    <>
      <Button data-aid={'editor-restore'} disabled={isSaveInProgress} onClick={restoreIvr}>
        Restore
      </Button>
      <Button
        data-aid={'editor-back-to-unpublished'}
        disabled={isSaveInProgress}
        type={Button.Types.SECONDARY}
        onClick={backToLatest}
      >
        Back to Latest
      </Button>
    </>
  );
};

const PublishButton: FC<any> = ({ dataAid, onPublish, disabled }) => {
  return (
    <Button data-aid={`${dataAid}`} onClick={onPublish} disabled={disabled}>
      Publish
    </Button>
  );
};

const StyledTooltip = styled(Tooltip)`
  margin: 0 8px;
`;

export default VersionManager;
